import React, { useEffect } from 'react';
import { Button } from '../components/ui/Button';
import { FloatingWhatsApp } from '../components/FloatingWhatsApp';
import { FloatingCta } from '../components/FloatingCta';
import { useMetaPixel } from '../hooks/useMetaPixel';
import { useDeadlineCountdown } from '../hooks/useDeadlineCountdown';
import { motion } from 'framer-motion';
import { AlertCircle, CheckCircle2, Flame, AlertTriangle, ChevronDown, XCircle, ShieldCheck, Zap, ArrowRight, Clock } from 'lucide-react';
const bonuses = [{
  title: 'LEAD SCRAPER BRAIN',
  value: '$997',
  desc: 'Encuentra negocios sin website en segundos.',
  bullets: ['teléfonos', 'emails', 'Instagram', 'WhatsApp'],
  footer: 'Listos para prospectar.',
  image: 'https://cdn.magicpatterns.com/uploads/dJfzTiuQYwUsv1t3US9VQy/03_bonus_1_lead_scraper.png'
}, {
  title: 'CUSTOM LLOYD AI CLOSER',
  value: '$497',
  desc: 'Tu asistente IA para responder objeciones y ayudarte a cerrar clientes.',
  bullets: ['copiar', 'pegar', 'enviar'],
  footer: 'Perfecto para personas sin experiencia en ventas.',
  image: 'https://cdn.magicpatterns.com/uploads/7BgYBgLPT2w2cJst6QECLC/bonus_2_custom_lloyd_FINAL.png'
}, {
  title: 'COMUNIDAD PRIVADA DE POR VIDA',
  value: '$497',
  desc: 'Acceso a la comunidad privada con:',
  bullets: ['soporte', 'actualizaciones', 'networking', 'ayuda en tiempo real'],
  footer: 'No estarás solo.',
  image: 'https://cdn.magicpatterns.com/uploads/w6XAsPTLjY4pq8ASqgBepN/05_bonus_3_community.png'
}, {
  title: 'GENERADOR DE PROPUESTAS CON IA',
  value: '$997',
  desc: 'Crea propuestas profesionales en minutos. Incluye:',
  bullets: ['propuestas listas', 'tablas de inversión', 'estructura profesional', 'automatización'],
  footer: 'Pasa de llamada → propuesta → cierre.',
  image: 'https://cdn.magicpatterns.com/uploads/pFmy6QjoBuztNTpkenBv4P/06_bonus_4_proposal_generator.png'
}, {
  title: 'STACK DE RECURSOS PRO',
  value: '$497',
  desc: 'Herramientas, plantillas y recursos listos para ejecutar.',
  bullets: [],
  image: 'https://cdn.magicpatterns.com/uploads/6ZL6ezBjhVx7ibUARCKVp1/07_bonus_5_resources_stack_FINAL_CINEMATIC.png'
}];
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
export function OfferPage() {
  const {
    trackCustomEvent
  } = useMetaPixel();
  const countdown = useDeadlineCountdown();
  const CHECKOUT_URL = 'https://www.fanbasis.com/agency-checkout/true-marketer/D9Vl5';
  // HeyLloyd click-tracking config.
  const HL_TRACK_URL = 'https://3000-i2zfhqgftgepb164zicho-fcdc7a09.us1.manus.computer/api/track/click';
  const HL_SLUG = 'a8ndg6is';
  useEffect(() => {
    trackCustomEvent('Offer-Page-Visit');
    // HeyLloyd: persist an incoming click id (?cid=) for the visit.
    try {
      const cid = new URLSearchParams(window.location.search).get('cid');
      if (cid) localStorage.setItem('hl_cid', cid);
    } catch (e) {}
  }, [trackCustomEvent]);
  // Append a click id to the checkout URL and navigate there.
  const goToCheckout = (clickId?: string) => {
    let href = CHECKOUT_URL;
    if (clickId) {
      const sep = href.includes('?') ? '&' : '?';
      href = href + sep + 'cid=' + clickId;
    }
    window.location.href = href;
  };
  const handleCheckout = () => {
    trackCustomEvent('Initiate-Checkout-297');
    // HeyLloyd: register the click (with ad ids + UTMs), then redirect to
    // checkout with the returned click id appended. Falls back to a plain
    // redirect if the tracking request fails so the CTA always works.
    let navigated = false;
    const params = new URLSearchParams(window.location.search);
    const go = (clickId?: string) => {
      if (navigated) return;
      navigated = true;
      goToCheckout(clickId);
    };
    // Safety net: never let a slow/blocked request strand the buyer.
    const fallback = window.setTimeout(() => go(), 1500);
    fetch(HL_TRACK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        slug: HL_SLUG,
        fbclid: params.get('fbclid') || '',
        gclid: params.get('gclid') || '',
        utm_source: params.get('utm_source') || '',
        utm_medium: params.get('utm_medium') || '',
        utm_campaign: params.get('utm_campaign') || '',
        utm_content: params.get('utm_content') || ''
      })
    }).then(r => r.json()).then(d => {
      window.clearTimeout(fallback);
      if (d && d.clickId) {
        localStorage.setItem('hl_cid', d.clickId);
        go(d.clickId);
      } else {
        go();
      }
    }).catch(() => {
      window.clearTimeout(fallback);
      go();
    });
  };
  const pad = (n: number) => String(n).padStart(2, '0');
  // CTA block: either the buy button or an "offer ended" notice
  const CtaBlock = ({
    className = ''
  }: {
    className?: string;
  }) => countdown.isExpired ? <div className={`mx-auto max-w-md bg-white/[0.03] backdrop-blur-2xl border border-white/10 rounded-2xl p-6 space-y-2 ${className}`} data-id="element-512" data-mp="3a6b8hdtdgz39">
        <p className="text-lg font-semibold text-white" data-id="element-513" data-mp="tr7rge0hnjgz">
          La oferta especial ha terminado
        </p>
        <p className="text-sm text-white/60" data-id="element-514" data-mp="18gdtsaumhpt3">
          Contáctanos por WhatsApp para conocer las opciones disponibles.
        </p>
      </div> : <div className={className} data-id="element-515" data-mp="13h9fj5sud1vw">
        <Button onClick={handleCheckout} size="lg" className="w-full sm:w-auto sm:min-w-[320px] text-base sm:text-xl font-bold h-16 sm:h-20 shadow-[0_0_50px_-12px_rgba(16,185,129,0.5)] ring-1 ring-emerald-400/40" rightIcon={<ArrowRight className="w-6 h-6" data-id="element-517" data-mp="ppw0o55vos77" />} data-id="element-516" data-mp="fr6uvar9ay7b">
          ACTIVA TU ACCESO AHORA
        </Button>
      </div>;
  return <div className="min-h-screen w-full bg-[#050505] text-white font-sans selection:bg-emerald-500/30 relative overflow-hidden" data-id="element-518" data-mp="kq07z22msn6y">
      {/* Animated Background Orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0" data-id="element-519" data-mp="1w5f4k4qxhd1j">
        <motion.div animate={{
        x: [0, 50, -30, 0],
        y: [0, -40, 60, 0],
        scale: [1, 1.1, 0.9, 1]
      }} transition={{
        duration: 18,
        repeat: Infinity,
        ease: 'linear'
      }} className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] bg-emerald-500/10 rounded-full blur-[120px]" data-id="element-520" data-mp="1w7j64g1ctzyn" />
        <motion.div animate={{
        x: [0, -60, 40, 0],
        y: [0, 50, -30, 0],
        scale: [1, 0.9, 1.1, 1]
      }} transition={{
        duration: 22,
        repeat: Infinity,
        ease: 'linear'
      }} className="absolute bottom-[-10%] right-[-10%] w-[60vw] h-[60vw] bg-emerald-400/5 rounded-full blur-[120px]" data-id="element-521" data-mp="2i5m4y5ah89v6" />
      </div>

      {/* Top Banner */}
      <div className="relative z-10 bg-red-500/10 backdrop-blur-xl border-b border-red-500/20 text-red-300 py-3 px-4 text-center flex items-center justify-center gap-2 font-medium text-sm tracking-wide" data-id="element-522" data-mp="1ue8b1uwog683">
        <AlertCircle className="w-4 h-4" data-id="element-523" data-mp="11pjgkeq4mz5b" />
        {countdown.isExpired ? 'OFERTA ESPECIAL CERRADA' : 'ACCESO RESERVADO POR TIEMPO LIMITADO'}
      </div>

      <main className="relative z-10 max-w-3xl mx-auto px-5 sm:px-6 py-12 sm:py-16 space-y-20 sm:space-y-24" data-id="element-524" data-mp="3jzyzpqhxaaz1">
        {/* Hero Section */}
        <motion.section initial="hidden" animate="visible" variants={fadeUp} className="text-center space-y-8 sm:space-y-10" data-id="element-525" data-mp="3fxaml6m6ehw9">
          <div className="space-y-4" data-id="element-526" data-mp="361hdjx7o16j8">
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-semibold tracking-tight text-white leading-[1.1]" data-id="element-527" data-mp="360fp8r1n2njk">
              <span className="block bg-gradient-to-r from-emerald-300 to-emerald-500 bg-clip-text text-transparent" data-id="element-528" data-mp="313fkcjgizqu1">
                48 HRS AI CASH MACHINE
              </span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-white/60 max-w-2xl mx-auto leading-relaxed font-medium" data-id="element-529" data-mp="2sac82ehm9k0z">
              El sistema completo para vender páginas web con IA a negocios
              locales… incluso si empiezas desde cero.
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-2 md:p-4 shadow-2xl shadow-black/50" data-id="element-530" data-mp="2ddlbse38oofl">
            <img src="https://cdn.magicpatterns.com/uploads/g4SAjMpoXz3BM6AyA6VfjW/AI_WEB_CLOSER.png" alt="AI Web Closer System" className="w-full rounded-2xl border border-white/10" data-id="element-531" data-mp="5z8tzf7gmv1u" />
          </div>

          {/* Deadline Countdown */}
          {!countdown.isExpired && <div className="bg-red-500/10 backdrop-blur-2xl border border-red-500/20 rounded-3xl p-5 sm:p-6 md:p-8 max-w-xl mx-auto" data-id="element-532" data-mp="117d90xib7tj">
              <div className="flex items-center justify-center gap-2 text-red-300 text-[11px] sm:text-xs uppercase tracking-widest font-semibold mb-4 text-center" data-id="element-533" data-mp="3ignj337alyna">
                <Clock className="w-4 h-4 shrink-0" data-id="element-534" data-mp="39j4tdlipew52" />
                El precio sube a $1997 el viernes en
              </div>
              <div className="flex justify-center items-baseline gap-1.5 sm:gap-3 md:gap-4" data-id="element-535" data-mp="3ehera3vtcaud">
                {countdown.days > 0 && <>
                    <div className="flex flex-col items-center min-w-0 flex-1 max-w-[70px]" data-id="element-536" data-mp="34iph6xa5ruoo">
                      <span className="text-3xl sm:text-4xl md:text-5xl font-semibold text-white tabular-nums tracking-tight" data-id="element-537" data-mp="2kx72mykz1ig0">
                        {pad(countdown.days)}
                      </span>
                      <span className="text-[9px] sm:text-[10px] uppercase tracking-widest text-red-300/80 mt-1" data-id="element-538" data-mp="2uv3g3x1dspha">
                        Días
                      </span>
                    </div>
                    <span className="text-3xl sm:text-4xl md:text-5xl font-semibold text-red-300/40" data-id="element-539" data-mp="33vt80pnitvpq">
                      :
                    </span>
                  </>}
                <div className="flex flex-col items-center min-w-0 flex-1 max-w-[70px]" data-id="element-540" data-mp="2zsdei26r0bh9">
                  <span className="text-3xl sm:text-4xl md:text-5xl font-semibold text-white tabular-nums tracking-tight" data-id="element-541" data-mp="3dl2yk12pv6bq">
                    {pad(countdown.hours)}
                  </span>
                  <span className="text-[9px] sm:text-[10px] uppercase tracking-widest text-red-300/80 mt-1" data-id="element-542" data-mp="3nkk1wbuhx84j">
                    Hrs
                  </span>
                </div>
                <span className="text-3xl sm:text-4xl md:text-5xl font-semibold text-red-300/40" data-id="element-543" data-mp="2rz4bdz0420y0">
                  :
                </span>
                <div className="flex flex-col items-center min-w-0 flex-1 max-w-[70px]" data-id="element-544" data-mp="2n59okmp7l5up">
                  <span className="text-3xl sm:text-4xl md:text-5xl font-semibold text-white tabular-nums tracking-tight" data-id="element-545" data-mp="2i66qxnbk61wr">
                    {pad(countdown.minutes)}
                  </span>
                  <span className="text-[9px] sm:text-[10px] uppercase tracking-widest text-red-300/80 mt-1" data-id="element-546" data-mp="3aw5jhsoyoh66">
                    Min
                  </span>
                </div>
                <span className="text-3xl sm:text-4xl md:text-5xl font-semibold text-red-300/40" data-id="element-547" data-mp="31uxlzcu9rkd5">
                  :
                </span>
                <div className="flex flex-col items-center min-w-0 flex-1 max-w-[70px]" data-id="element-548" data-mp="2wzi8wn435shs">
                  <span className="text-3xl sm:text-4xl md:text-5xl font-semibold text-white tabular-nums tracking-tight" data-id="element-549" data-mp="1qf0p92g6vw5d">
                    {pad(countdown.seconds)}
                  </span>
                  <span className="text-[9px] sm:text-[10px] uppercase tracking-widest text-red-300/80 mt-1" data-id="element-550" data-mp="xvbland6t3q">
                    Seg
                  </span>
                </div>
              </div>
            </div>}

          <div className="pt-4" data-id="element-551" data-mp="9ytjw54u5ms4">
            {countdown.isExpired ? <CtaBlock data-id="element-552" data-mp="es64m46e99fl" /> : <Button onClick={handleCheckout} size="lg" className="w-full sm:w-auto sm:min-w-[320px] text-base sm:text-lg font-bold h-16 shadow-[0_0_40px_-10px_rgba(16,185,129,0.4)] ring-1 ring-emerald-400/30" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-554" data-mp="orn94i9wth9z" />} data-id="element-553" data-mp="esnwyppxr3i8">
                ACTIVA TU ACCESO AHORA
              </Button>}
          </div>
        </motion.section>

        {/* Core Offer */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeUp} className="bg-white/[0.03] backdrop-blur-2xl rounded-3xl shadow-2xl shadow-black/50 border border-white/10 p-6 sm:p-8 md:p-12 relative overflow-hidden" data-id="element-555" data-mp="xpp4oiavjhpv">
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" data-id="element-556" data-mp="6nn6z76tbnnb" />
          <div className="flex items-center gap-3 sm:gap-4 mb-8 sm:mb-10 border-b border-white/10 pb-6 sm:pb-8" data-id="element-557" data-mp="1q4xnfovv6es">
            <CheckCircle2 className="w-7 h-7 sm:w-8 sm:h-8 text-emerald-400 shrink-0" data-id="element-558" data-mp="flgt5pav50wr" />
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight" data-id="element-559" data-mp="boatpgo0dibq">
              LO QUE ACTIVAS HOY
            </h2>
          </div>

          <div className="space-y-6 sm:space-y-8" data-id="element-560" data-mp="klv1z9kagevu">
            <div data-id="element-561" data-mp="kl1xygcps3he">
              <h3 className="text-xl sm:text-2xl font-semibold text-white tracking-tight" data-id="element-562" data-mp="zbiyzylfsrdr">
                AI WEB CLOSER SYSTEM™
              </h3>
              <p className="text-emerald-400/80 font-medium mt-1" data-id="element-563" data-mp="ud7ormr8cev0">
                Valor $1,997
              </p>
            </div>
            <p className="text-white/70 text-base sm:text-lg" data-id="element-564" data-mp="13de1h6f3tat4">
              El sistema paso a paso que te muestra cómo:
            </p>
            <ul className="space-y-4 sm:space-y-5" data-id="element-565" data-mp="1mywayqpfowux">
              {['Encontrar negocios que NECESITAN websites', 'Crear páginas web con IA en aproximadamente 10 minutos', 'Conseguir clientes usando mensajes simples', 'Cerrar clientes por WhatsApp', 'Cobrar entre $300 y $1,500 por proyecto', 'Escalar usando automatización e IA'].map((item, i) => <li key={i} className="flex items-start gap-3 sm:gap-4" data-id="element-566" data-mp="1hy9rll7cypnt" data-mpi={i}>
                  <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400 shrink-0 mt-0.5" data-id="element-567" data-mp="147n0of7ua42a" />
                  <span className="text-white/80 text-base sm:text-lg" data-id="element-568" data-mp="1906gzjxwvdas">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>
        </motion.section>

        {/* Not Just a Course */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeUp} className="text-center space-y-6 sm:space-y-8 bg-white/[0.02] backdrop-blur-3xl rounded-3xl p-6 sm:p-8 md:p-12 shadow-2xl border border-white/5 relative overflow-hidden" data-id="element-569" data-mp="z7imivuellf8">
          <div className="absolute inset-0 bg-emerald-500/5 blur-3xl" data-id="element-570" data-mp="d94gbb4a7gcp" />
          <div className="relative z-10" data-id="element-571" data-mp="d9wb4jq21a1x">
            <div className="flex justify-center mb-6" data-id="element-572" data-mp="8hw6wf2ybuzj">
              <Flame className="w-10 h-10 sm:w-12 sm:h-12 text-orange-500/80" data-id="element-573" data-mp="8gl1katuyf6b" />
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight mb-4" data-id="element-574" data-mp="4gkgi7c975qa">
              ESTO NO ES SOLO UN CURSO
            </h2>
            <p className="text-lg sm:text-xl text-white/60 mb-6" data-id="element-575" data-mp="3qnrdfr974yy5">
              Estás activando:
            </p>
            <p className="text-lg sm:text-xl md:text-2xl font-medium leading-relaxed text-emerald-400" data-id="element-576" data-mp="3lpz8nwngdicj">
              un sistema • herramientas • automatizaciones • prompts • scripts •
              estructuras • IA entrenada • y un proceso diseñado para reducir
              errores y acelerar resultados.
            </p>
          </div>
        </motion.section>

        {/* Bonuses */}
        <section className="space-y-10 sm:space-y-12" data-id="element-577" data-mp="2xue42w858td6">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-center mb-10 sm:mb-12" data-id="element-578" data-mp="2xrrwwxp60fvh">
            Tus Bonos de Acción Rápida
          </motion.h2>

          {bonuses.map((bonus, i) => <motion.div key={i} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={fadeUp} className="bg-white/[0.03] backdrop-blur-2xl rounded-3xl shadow-2xl shadow-black/50 border border-white/10 overflow-hidden" data-id="element-579" data-mp="2euqf2caiila4" data-mpi={i}>
              <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" data-id="element-580" data-mp="2nxrjm8jqc4bo" />
              {/* Bonus Image */}
              <div className="w-full bg-black/40 border-b border-white/10 p-3 sm:p-4 md:p-8 flex justify-center" data-id="element-581" data-mp="34eauv9o5u3q">
                <img src={bonus.image} alt={bonus.title} className="w-full max-w-2xl rounded-2xl border border-white/10 shadow-2xl" data-id="element-582" data-mp="82plh87sg8n0" />
              </div>

              {/* Bonus Content */}
              <div className="p-6 sm:p-8 md:p-10 space-y-5 sm:space-y-6" data-id="element-583" data-mp="20524fwtuoo1t">
                <div data-id="element-584" data-mp="2e5v04kvw1p6i">
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight text-white mb-2" data-id="element-585" data-mp="2e4a0igupvg1s">
                    BONUS #{i + 1} — {bonus.title}
                  </h3>
                  <p className="text-emerald-400 font-medium text-base sm:text-lg" data-id="element-586" data-mp="1lhz2ndyhvt8o">
                    Valor: {bonus.value}
                  </p>
                </div>
                <p className="text-white/70 text-base sm:text-lg leading-relaxed" data-id="element-587" data-mp="1v6h1hpn7rq9o">
                  {bonus.desc}
                </p>
                {bonus.bullets.length > 0 && <ul className="flex flex-wrap gap-2 sm:gap-3 pt-2" data-id="element-588" data-mp="1qepgdumtucqx">
                    {bonus.bullets.map((b, j) => <li key={j} className="flex items-center gap-2 bg-white/5 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium text-white/80 border border-white/10" data-id="element-589" data-mp="12oldjphgqh0z" data-mpi={j}>
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" data-id="element-590" data-mp="39d5c76ew6a32" />
                        {b}
                      </li>)}
                  </ul>}
                {bonus.footer && <p className="text-white/50 italic pt-3 sm:pt-4 text-base sm:text-lg" data-id="element-591" data-mp="30etcmew1s1yh">
                    {bonus.footer}
                  </p>}
              </div>
            </motion.div>)}

          {/* Special Bonus */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="relative bg-white/[0.04] backdrop-blur-2xl rounded-3xl p-6 sm:p-8 md:p-12 shadow-2xl shadow-emerald-900/20 border border-emerald-500/30 overflow-hidden mt-10 sm:mt-12" data-id="element-592" data-mp="3s6hkao3vs0az">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-transparent" data-id="element-593" data-mp="3o29xwxkwawfx" />
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent" data-id="element-594" data-mp="27nrsdzdhzg83" />

            <div className="absolute top-0 right-0 p-8 opacity-5" data-id="element-595" data-mp="2cm32mb7pfsqu">
              <Zap className="w-48 h-48" data-id="element-596" data-mp="33g2kiz7jzpk4" />
            </div>

            <div className="relative z-10 space-y-5 sm:space-y-6" data-id="element-597" data-mp="2zcedqiqqwxzx">
              <div className="inline-block bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wider" data-id="element-598" data-mp="2zfabo72lfmod">
                Bonus Especial en Vivo
              </div>
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight" data-id="element-599" data-mp="3n30b9g34izvo">
                AI ADS CASH MACHINE™
              </h3>
              <p className="text-emerald-400 font-medium text-base sm:text-lg" data-id="element-600" data-mp="3i472g0ys7x1u">
                Valor: $1,997
              </p>
              <p className="text-white/70 text-lg sm:text-xl leading-relaxed max-w-2xl" data-id="element-601" data-mp="38dm145rq4ef8">
                Aprende cómo ayudar negocios locales a conseguir clientes usando
                publicidad digital.
              </p>
              <p className="text-white/90 font-medium bg-white/5 p-5 sm:p-6 rounded-2xl border border-white/10 text-base sm:text-lg" data-id="element-602" data-mp="4wluzahdudu4">
                Y lo más importante: cómo convertir clientes de websites… en
                clientes mensuales recurrentes.
              </p>
            </div>
          </motion.div>
        </section>

        {/* Value Stack & Price */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="bg-white/[0.03] backdrop-blur-3xl rounded-3xl shadow-2xl shadow-emerald-900/20 border border-emerald-400/30 p-6 sm:p-8 md:p-16 text-center space-y-8 sm:space-y-10 relative overflow-hidden" data-id="element-603" data-mp="r5avwlx8ulzx">
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent" data-id="element-604" data-mp="14w5xre9bs2u0" />

          <div className="absolute top-0 left-1/2 -translate-x-1/2 bg-emerald-500/20 backdrop-blur-md border border-emerald-500/30 border-t-0 text-emerald-300 px-5 sm:px-8 py-2 rounded-b-2xl font-bold text-xs sm:text-sm tracking-widest whitespace-nowrap" data-id="element-605" data-mp="19uh77nvpkbbp">
            RESUMEN DE TU INVERSIÓN
          </div>

          <div className="pt-10 sm:pt-8 space-y-3" data-id="element-606" data-mp="2ie5awfpw629b">
            <p className="text-white/50 font-medium uppercase tracking-widest text-xs sm:text-sm" data-id="element-607" data-mp="2nb5fsnb08yyu">
              Valor Total
            </p>
            <p className="text-3xl sm:text-4xl font-semibold text-white/40 line-through decoration-red-500/50 decoration-4" data-id="element-608" data-mp="2s96znag992bd">
              $5,482+
            </p>
          </div>

          <div className="space-y-5 sm:space-y-6" data-id="element-609" data-mp="31aexjrev014x">
            <div className="flex justify-center" data-id="element-610" data-mp="319lvv4urc1tn">
              <Flame className="w-9 h-9 sm:w-10 sm:h-10 text-orange-500/80" data-id="element-611" data-mp="369htmmcv2x0r" />
            </div>
            <p className="text-emerald-400 font-bold uppercase tracking-widest text-xs sm:text-sm" data-id="element-612" data-mp="3b2awt5solxaq">
              {countdown.isExpired ? 'Precio Regular' : 'Hoy Solo'}
            </p>
            <div className="text-6xl sm:text-7xl md:text-8xl font-semibold tracking-tight bg-gradient-to-b from-white to-white/80 bg-clip-text text-transparent" data-id="element-613" data-mp="3k2g0p5yo2dk5">
              {countdown.isExpired ? '$1997' : '$497'}{' '}
              <span className="text-2xl sm:text-3xl text-white/40 font-medium" data-id="element-614" data-mp="1degtyskijumd">
                USD
              </span>
            </div>
          </div>

          <div className="pt-6 sm:pt-8" data-id="element-615" data-mp="14czcp6vddmw3">
            <CtaBlock data-id="element-616" data-mp="14dsedtfh1m7d" />
          </div>
        </motion.section>

        {/* Important Note */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="bg-amber-500/5 backdrop-blur-xl rounded-3xl p-6 sm:p-8 md:p-10 border border-amber-500/20 relative overflow-hidden" data-id="element-617" data-mp="zexx4vx5k5pd">
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" data-id="element-618" data-mp="1s4xw6e4z2okb" />
          <div className="flex items-center gap-3 sm:gap-4 mb-5 sm:mb-6" data-id="element-619" data-mp="1ida9eqjd1vp6">
            <AlertTriangle className="w-7 h-7 sm:w-8 sm:h-8 text-amber-400 shrink-0" data-id="element-620" data-mp="2fs5xym36zfhl" />
            <h3 className="text-xl sm:text-2xl font-semibold text-amber-200 tracking-tight" data-id="element-621" data-mp="2fu8uzq2ri8vt">
              IMPORTANTE
            </h3>
          </div>
          <p className="text-amber-100/80 text-lg sm:text-xl mb-6 sm:mb-8 leading-relaxed" data-id="element-622" data-mp="2txepefd0ndku">
            No estás pagando por "información". Estás activando:
          </p>
          <div className="flex flex-wrap gap-2 sm:gap-3" data-id="element-623" data-mp="2l5mg47zqaahu">
            {['un sistema', 'herramientas', 'automatización', 'IA', 'estructura', 'velocidad', 'claridad'].map((item, i) => <span key={i} className="bg-amber-500/10 text-amber-200 px-3 sm:px-4 py-2 rounded-full font-medium text-xs sm:text-sm border border-amber-500/20" data-id="element-624" data-mp="2p4v9vcurq8de" data-mpi={i}>
                {item}
              </span>)}
          </div>
        </motion.section>

        {/* Webinar Context */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="text-center space-y-6 sm:space-y-8 max-w-2xl mx-auto py-4 sm:py-8" data-id="element-625" data-mp="22834qxpssfs9">
          <div className="flex justify-center" data-id="element-626" data-mp="fncgappunl1">
            <ChevronDown className="w-9 h-9 sm:w-10 sm:h-10 text-white/20 animate-bounce" data-id="element-627" data-mp="4gh0d435s2dx" />
          </div>
          <h3 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white" data-id="element-628" data-mp="1oy9og3dq9k9a">
            SI ESTO TE HIZO SENTIDO EN EL WEBINAR…
          </h3>
          <p className="text-lg sm:text-xl text-white/60" data-id="element-629" data-mp="1kz0v2zmlnoe9">
            Entonces ya sabes algo: El problema nunca fue tu esfuerzo.
          </p>
          <p className="text-2xl sm:text-3xl font-semibold bg-gradient-to-r from-emerald-300 to-emerald-500 bg-clip-text text-transparent" data-id="element-630" data-mp="1txcjcuzzua3j">
            Era NO tener un sistema claro.
          </p>
        </motion.section>

        {/* Who it's for / Not for */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="grid md:grid-cols-2 gap-6 sm:gap-8" data-id="element-631" data-mp="23ps23uj472ph">
          <div className="bg-white/[0.03] backdrop-blur-2xl rounded-3xl p-6 sm:p-8 md:p-10 shadow-2xl border border-white/10 relative overflow-hidden" data-id="element-632" data-mp="2cpng07421bt7">
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" data-id="element-633" data-mp="iacl12l7pqp4" />
            <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8" data-id="element-634" data-mp="kprev20182jp">
              <CheckCircle2 className="w-7 h-7 sm:w-8 sm:h-8 text-emerald-400 shrink-0" data-id="element-635" data-mp="oqbag14yiy3q" />
              <h3 className="text-lg sm:text-xl font-semibold text-white tracking-tight" data-id="element-636" data-mp="tpo8zj56xtm5">
                ESTE SISTEMA ES PARA TI SI:
              </h3>
            </div>
            <ul className="space-y-4 sm:space-y-5" data-id="element-637" data-mp="ynzhnqjr1y2s">
              {['Quieres ingresos online reales', 'Quieres vender servicios simples', 'Quieres aprovechar la IA', 'Quieres evitar modelos complicados', 'Quieres una oportunidad con alta demanda', 'Estás dispuesto a ejecutar'].map((item, i) => <li key={i} className="flex items-start gap-3 sm:gap-4" data-id="element-638" data-mp="ayen0ae8hcdo" data-mpi={i}>
                  <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400/80 shrink-0 mt-0.5" data-id="element-639" data-mp="fq6eqo94lo5e" />
                  <span className="text-white/80 text-base sm:text-lg" data-id="element-640" data-mp="3egonpkw7y30o">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>

          <div className="bg-white/[0.03] backdrop-blur-2xl rounded-3xl p-6 sm:p-8 md:p-10 shadow-2xl border border-white/10 relative overflow-hidden" data-id="element-641" data-mp="3bv77ep03fwhe">
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" data-id="element-642" data-mp="36wx9w7qwcjsm" />
            <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8" data-id="element-643" data-mp="31z461ld2g1vi">
              <XCircle className="w-7 h-7 sm:w-8 sm:h-8 text-red-400 shrink-0" data-id="element-644" data-mp="2wxonbzyd27ga" />
              <h3 className="text-lg sm:text-xl font-semibold text-white tracking-tight" data-id="element-645" data-mp="2sxefehr0d6ys">
                ESTE SISTEMA NO ES PARA TI SI:
              </h3>
            </div>
            <ul className="space-y-4 sm:space-y-5" data-id="element-646" data-mp="2j6a2dnkw7w6l">
              {['Buscas dinero mágico', 'No quieres ejecutar', 'Quieres resultados sin acción', 'No estás dispuesto a aprender algo nuevo'].map((item, i) => <li key={i} className="flex items-start gap-3 sm:gap-4" data-id="element-647" data-mp="2995bjag3rcjg" data-mpi={i}>
                  <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-400/80 shrink-0 mt-0.5" data-id="element-648" data-mp="29agej81q4g9i" />
                  <span className="text-white/80 text-base sm:text-lg" data-id="element-649" data-mp="259eeiozzg06h">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>
        </motion.section>

        {/* Guarantee */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="bg-white/[0.03] backdrop-blur-2xl rounded-3xl p-8 sm:p-10 md:p-16 border border-white/10 text-center space-y-6 sm:space-y-8 relative overflow-hidden" data-id="element-650" data-mp="1lliaqy2sr5y6">
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" data-id="element-651" data-mp="2ibhqbchd74q9" />
          <div className="absolute inset-0 bg-emerald-500/5 blur-3xl" data-id="element-652" data-mp="24kmfvvr7wfkk" />
          <div className="relative z-10" data-id="element-653" data-mp="24i9wde5t9x5e">
            <div className="flex justify-center mb-6" data-id="element-654" data-mp="29i5skr89okae">
              <div className="relative" data-id="element-655" data-mp="2x8s0mii9u4ke">
                <div className="absolute inset-0 bg-emerald-500/30 blur-xl rounded-full" data-id="element-656" data-mp="2x7qoeapirc13" />
                <ShieldCheck className="w-16 h-16 sm:w-20 sm:h-20 text-emerald-400 relative z-10" data-id="element-657" data-mp="31zqhpkb336op" />
              </div>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-white tracking-tight mb-5 sm:mb-6" data-id="element-658" data-mp="3bch92df21di6">
              GARANTÍA DE 30 DÍAS
            </h2>
            <p className="text-lg sm:text-xl text-white/70 max-w-xl mx-auto leading-relaxed" data-id="element-659" data-mp="3l4danygcnlr5">
              Prueba AI WEB CLOSER™ sin riesgo. Tu acceso está protegido con
              garantía de satisfacción de 30 días.
            </p>
          </div>
        </motion.section>

        {/* Final CTA */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="text-center space-y-8 sm:space-y-10 pb-12 sm:pb-16" data-id="element-660" data-mp="2oew3ff1pxrnc">
          <div className="flex justify-center" data-id="element-661" data-mp="2xe9g0gl52wca">
            <Zap className="w-14 h-14 sm:w-16 sm:h-16 text-emerald-400/80" data-id="element-662" data-mp="2xglw0o7c6wmp" />
          </div>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight text-white" data-id="element-663" data-mp="hljy9ozb52yk">
            TU OPORTUNIDAD ES AHORA
          </h2>
          <div className="text-lg sm:text-xl md:text-2xl text-white/60 space-y-3 max-w-2xl mx-auto leading-relaxed" data-id="element-664" data-mp="2751xe3x3myi0">
            <p data-id="element-665" data-mp="2b75b6z78qf6g">Millones de negocios todavía necesitan websites.</p>
            <p data-id="element-666" data-mp="2g5gndgl0d1ru">La IA está cambiando el mercado rápidamente.</p>
            <p className="font-semibold text-white pt-4 sm:pt-6" data-id="element-667" data-mp="2g3vsfpus7gtg">
              Y las personas que aprendan esto primero… van a tener ventaja.
            </p>
          </div>

          <div className="pt-8 sm:pt-10" data-id="element-668" data-mp="2pu88p5vkcnts">
            <CtaBlock data-id="element-669" data-mp="2pxcj4kzmgfsw" />
          </div>
        </motion.section>
      </main>

      {/* Footer */}
      <footer className="relative z-10 pb-8 text-center text-white/40 text-sm" data-id="element-670" data-mp="2uvxp711k1tp9">
        &copy; {new Date().getFullYear()} 48 Hrs AI Cash Machine. Todos los
        derechos reservados.
      </footer>

      {!countdown.isExpired && <FloatingCta onClick={handleCheckout} price="$497" originalPrice="$1997" label="ACTIVAR" data-id="element-671" data-mp="14i6tll2exj0c" />}

      <FloatingWhatsApp link="https://wa.link/infocommerce" data-id="element-672" data-mp="19g8bw3s0lear" />
    </div>;
}